// Full file: FeedbackDialog.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Typography, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  FormControl, 
  Checkbox, 
  FormGroup, 
  Box, 
  IconButton,
  CircularProgress,
  useTheme
} from '@mui/material';
import { 
  IconMessageCircle2, 
  IconCheck, 
  IconSend, 
  IconX, 
  IconMail, 
  IconMoodSmile 
} from '@tabler/icons-react';
import { submitFeedback } from 'src/services/api';

const FeedbackDialog = ({ open, handleClose }) => {
  const theme = useTheme();
  const [feedbackText, setFeedbackText] = useState('');
  const [satisfaction, setSatisfaction] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isLight = theme.palette.mode === 'light';

  const handleFeedbackChange = (event) => {
    setFeedbackText(event.target.value);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const feedbackData = {
      name: isAnonymous ? 'Anonymous' : name,
      satisfaction,
      feedback: feedbackText,
    };

    try {
      await submitFeedback(feedbackData);
      setIsSubmitted(true); 
    } catch (error) {
      // Error handled
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setIsSubmitted(false);
    setFeedbackText('');
    setSatisfaction('');
    setName('');
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCloseDialog} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '8px',
          boxShadow: theme.shadows[10],
          border: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none',
          backgroundColor: 'background.paper'
        }
      }}
    >
      <Box sx={{ position: 'absolute', right: 16, top: 16 }}>
        <IconButton onClick={handleCloseDialog} size="small" sx={{ color: 'text.secondary' }}>
          <IconX size={20} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 3 }}>
        <Box 
          sx={{ 
            width: 72, 
            height: 72, 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 2,
            bgcolor: isSubmitted ? 'success.light' : 'primary.light',
            color: isSubmitted ? 'success.main' : 'primary.main',
            boxShadow: isSubmitted 
              ? `0 0 0 4px ${theme.palette.success.light}` 
              : `0 0 20px ${isLight ? 'rgba(93, 135, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)'}`,
            transition: 'all 0.3s ease-in-out'
          }}
        >
          {isSubmitted ? (
            <IconCheck size={36} stroke={1.5} />
          ) : (
            <IconMessageCircle2 size={36} stroke={1.5} />
          )}
        </Box>

        <DialogTitle sx={{ p: 0, mb: 1, fontWeight: 700, fontSize: '1.25rem', textAlign: 'center', color: 'text.primary' }}>
          {isSubmitted ? "Thank You!" : "We Value Your Feedback"}
        </DialogTitle>
      </Box>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {isSubmitted ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" paragraph sx={{ fontSize: '1rem', color: 'text.primary' }}>
              Your feedback means a lot to us. Every piece of feedback helps make ZOSA better for everyone.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We appreciate you taking the time to share your thoughts. Thank you for being part of our journey! 
            </Typography>
          </Box>
        ) : (
          <>
            <Box 
              sx={{ 
                bgcolor: isLight ? 'info.light' : 'rgba(83, 155, 255, 0.1)',
                p: 2, 
                borderRadius: '12px', 
                mb: 3,
                border: '1px solid',
                borderColor: isLight ? 'info.main' : 'rgba(83, 155, 255, 0.3)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <IconMail size={18} color={isLight ? theme.palette.info.main : theme.palette.info.light} />
                <Typography variant="subtitle2" fontWeight={700} color={isLight ? 'info.dark' : 'info.light'}>
                   Note on Support
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                This form is for feedback only. For urgent account issues or credit support, please visit the{' '}
                <Typography 
                  component={Link} 
                  to="/contact" 
                  onClick={handleClose} // Close dialog when navigating
                  sx={{ 
                    color: 'primary.main', 
                    textDecoration: 'none!important',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 'inherit'
                  }}
                >
                  contact page
                </Typography>.
              </Typography>
            </Box>

            <FormGroup sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAnonymous}
                    onChange={() => setIsAnonymous(!isAnonymous)}
                    disabled={isSubmitting}
                    sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                  />
                }
                label={<Typography variant="body2" color="text.primary">Submit anonymously</Typography>}
              />
            </FormGroup>

            <Box sx={{ height: isAnonymous ? 0 : 'auto',  transition: 'all 0.3s ease', mb: isAnonymous ? 0 : 3 }}>
              {!isAnonymous && (
                <TextField
                  fullWidth
                  label="Your Name"
                  placeholder="How should we address you?"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                  InputProps={{ sx: { borderRadius: '12px' } }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'divider' },
                    }
                  }}
                />
              )}
            </Box>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
              <IconMoodSmile size={20} /> How was your experience?
            </Typography>
            
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <RadioGroup
                value={satisfaction}
                onChange={(e) => setSatisfaction(e.target.value)}
                row
              >
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={0.5}
                  width="100%"
                  sx={{ 
                    p: 1, 
                    borderRadius: '12px', 
                    bgcolor: isLight ? 'action.hover' : 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                   {['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'].map((label) => (
                      <FormControlLabel 
                        key={label}
                        value={label} 
                        control={<Radio size="small" sx={{ color: 'primary.main' }} />} 
                        label={<Typography variant="caption" sx={{ lineHeight: 1.2, color: 'text.primary' }}>{label}</Typography>} 
                        disabled={isSubmitting}
                        sx={{ 
                          m: 0, 
                          flex: 1, 
                          justifyContent: 'center', 
                          textAlign: 'center',
                          '.MuiFormControlLabel-label': { mt: 0.5 } 
                        }}
                        labelPlacement="bottom"
                      />
                   ))}
                </Box>
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              label="Your feedback"
              placeholder="Tell us what you liked or how we can improve..."
              variant="outlined"
              multiline
              rows={4}
              value={feedbackText}
              onChange={handleFeedbackChange}
              disabled={isSubmitting}
              InputProps={{ sx: { borderRadius: '12px' } }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'divider' },
                }
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              For bugs, please include your device, browser, and steps to reproduce the issue.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, flexDirection: 'column', gap: 1 }}>
        {isSubmitted ? (
          <Button 
            onClick={handleCloseDialog} 
            color="primary" 
            variant="contained" 
            fullWidth
            size="large"
            sx={{ borderRadius: '8px', boxShadow: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Button 
              onClick={handleCloseDialog} 
              disabled={isSubmitting}
              variant="text"
              sx={{ flex: 1, color: 'text.secondary', borderRadius: '8px' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={!satisfaction || !feedbackText || isSubmitting}
              sx={{ 
                flex: 2, 
                borderRadius: '8px', 
                fontWeight: 600,
                boxShadow: theme.shadows[4]
              }}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <IconSend size={18} />}
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;